// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { getReferenceString } from '@medplum/core';
import type { Consent, Patient, QuestionnaireResponse } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { CONSENT_DEFINITIONS, findConsentForDefinition, questionnaireResponseToValues } from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';

export interface UsePatientConsentsResult {
  consents: Consent[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  setConsents: (consents: Consent[]) => void;
}

function mergeIntakeInformedConsent(
  consents: Consent[],
  questionnaireResponses: QuestionnaireResponse[],
  patient: Patient
): Consent[] {
  const definition = CONSENT_DEFINITIONS.find((item) => item.key === 'informed-consent');
  if (!definition) {
    return consents;
  }
  if (findConsentForDefinition(consents, definition)) {
    return consents;
  }

  const latestSignedResponse = [...questionnaireResponses]
    .sort((a, b) => (b.authored ?? '').localeCompare(a.authored ?? ''))
    .find((response) => questionnaireResponseToValues(response)['consent-signed'] === true);

  if (!latestSignedResponse) {
    return consents;
  }

  return [
    ...consents,
    {
      resourceType: 'Consent',
      patient: { reference: getReferenceString(patient) },
      status: 'active',
      scope: definition.scope,
      category: [definition.category],
      policyRule: definition.policyRule,
      dateTime: latestSignedResponse.authored ?? new Date().toISOString(),
    },
  ];
}

export function usePatientConsents(patient: Patient | undefined): UsePatientConsentsResult {
  const medplum = useMedplum();
  const [consents, setConsents] = useState<Consent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [results, questionnaireResponses] = await Promise.all([
        medplum.searchResources('Consent', {
          patient: getReferenceString(patient),
          _sort: '-_lastUpdated',
          _count: '50',
        }),
        medplum.searchResources('QuestionnaireResponse', {
          subject: getReferenceString(patient),
          _sort: '-authored',
          _count: '50',
        }),
      ]);
      setConsents(mergeIntakeInformedConsent(results, questionnaireResponses, patient));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load consents'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { consents, loading, error, refresh: fetch, setConsents };
}
